import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, Signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeletedEntity } from '@app/_interfaces/feature.interface';
import { createEntry } from '@app/_models/entry.model';
import { FocusSetup } from '@app/_interfaces/focus-tracker.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { UtilsService } from '@app/_services/utils.service';
import { FocusStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { FocusEntriesComponent } from '../focus-entries/focus-entries.component';
import { FocusBlockComponent } from '../focus-block/focus-block.component';
import { FocusSetupFormComponent } from '../focus-setup-form/focus-setup-form.component';
import { ModalService } from '@app/_services/modal.service';


@Component({
    selector: 'app-focus-trash-tab',
    imports: [
        CommonModule,
        SvgComponent,
        FocusBlockComponent
    ],
    templateUrl: './focus-trash-tab.component.html',
    styleUrl: './focus-trash-tab.component.scss'
})

export class FocusTrashTabComponent {

  readonly focusStore = inject(FocusStore);
  deletedFocusSetups: Signal<DeletedEntity[]> = computed(() => {
    const deletedEntities: DeletedEntity[] = [];
    this.focusStore.setups().forEach(setup => {
      if (setup.deletedAt && !setup.permaDeletedAt && setup.uid === this.cc.user.uid) {
        deletedEntities.push({
          entityName: 'focus',
          entityId: setup.id,
          deletedAt: this.utils.toDate(setup.deletedAt),
          entries: this.ds.getAllDatesOfEntity({ ...setup, startTime: null, timeZoneId: null }, 'focus', this.cc.user.uid),
          entityEntry: createEntry({
            setupId: setup.id,
            entityType: 'focus',
            date: '',
            occurDate: '',
            sortTitle: setup.title,
            isScheduled: !!setup.startDate,
            sortCreatedAt: setup.createdAt ?? null,
            tagIds: setup.tags || [],
          }),
          isRepeated: !!setup.repeat
        });
      }
    });

    deletedEntities.sort((a, b) => {
      return b.deletedAt.getTime() - a.deletedAt.getTime();
    });
    return deletedEntities;
  });
  show: WritableSignal<string[]> = signal(['label', 'time', 'reminder', 'repeat', 'collaboratorsCount', 'deletedAt']);

  constructor(
    public ss: SettingsService,
    private alertService: AlertService,
    public dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private utils: UtilsService,
    private modalService: ModalService
  ) { }

  trackByEntity(index: number, entity: DeletedEntity) {
    return entity.entityId;
  };

  openSetup(entityId: string) {
    const setup = this.focusStore.setupsMap()[entityId];

    const data = {
      mode: 'edit',
      value: setup
    };

    if (this.cc.isNarrowScreen()) {
      this.modalService.openMainContent(
        FocusSetupFormComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    } else {
      this.modalService.openLevel1(
        FocusSetupFormComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    }
  };

  openEntries(entityId: string) {
    this.modalService.openLevel2(
      FocusEntriesComponent,
      { entityId: entityId },
      { disableClose: false, autoFocus: false }
    );
  };

  async remove(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const setup = this.focusStore.setupsMap()[entityId];
    const updatedSetup: FocusSetup = { ...setup, permaDeletedAt: new Date() };
    this.focusStore.updateSetup(updatedSetup);
  };

  async restore(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const setup = this.focusStore.setupsMap()[entityId];
    const updatedSetup: FocusSetup = { ...setup, deletedAt: null };
    this.focusStore.updateSetup(updatedSetup);
    const isShow = await this.alertService.toast('trashItemRestored_content', 'common_show', 'common_dismiss');
    if (isShow) {
      this.openSetup(entityId);
    }
  };
}
